"use client";

import React, { Fragment } from "react";
import ScrollReveal from "@/components/common/ScrollReveal";

interface Tool {
  title: string;
  subtitle: string;
  description: string;
}

const tools: Tool[] = [
  {
    title: "Credential Scanner",
    subtitle: "Hunt Forgotten Secrets",
    description:
      "ECQ’s red team credential scanner sweeps documents, PDFs, notes, and shared drives for passwords and keys hiding in plain sight — the same loot attackers grab once they’re inside",
  },
  {
    title: "Attack Validation",
    subtitle: "Simulate Real Attacks",
    description:
      "ECQ’s attack validation tool emulates real adversary techniques against your environment to expose configuration gaps, weak detections, and lateral movement paths before attackers find them",
  },
  {
    title: "Exploit Intelligence",
    subtitle: "Sharper Detection Telemetry",
    description:
      "Continuous exploit intelligence from ECQ’s offensive research feeds directly into detection engineering — every new attacker technique becomes a new hunt and a new detection rule.",
  },
];

const deliverySteps = [
  "Exploit Intelligence",
  "Detection Engineering",
  "Threats Hunting",
  "Security Monitoring",
  "Customer Defense",
];

const benefits: { label: string; description: string }[] = [
  {
    label: "Catch what others miss:",
    description: "Detections built from real attacker techniques.",
  },
  {
    label: "Validated, not assumed:",
    description: "Proven against live attack simulations.",
  },
  {
    label: "Always current:",
    description: "Continuous red team research feeds telemetry.",
  },
  {
    label: "One unified partner:",
    description: "Offense and defense from a single team.",
  },
];

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-[16px] font-bold uppercase tracking-[0.2px] text-text-highlight">
    {children}
  </h2>
);

const OffensiveMDR = () => {
  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pb-20 pt-2">
      <div className="relative z-10 mx-auto flex w-full max-w-[1300px] flex-col gap-16 lg:gap-[70px]">
        {/* Hero copy */}
        <ScrollReveal direction="up" delay={0}>
          <div className="flex flex-col gap-4 text-text-highlight">
            <h2 className="text-[24px] sm:text-[30px] font-bold uppercase leading-tight tracking-[0.15px]">
              Think like the attacker.
              <br />
              Detect like no one else.
            </h2>
            <div className="flex max-w-[530px] flex-col gap-6 sm:gap-7">
              <p className="body-1">
                Most MDR providers monitor defensive telemetry &mdash; alerts, logs, and signatures.
                They tell you what their tools can see, not what attackers actually do. The result:
                blind spots that adversaries routinely exploit.
              </p>
              <p className="body-1">
                ECQ is a red team that runs an MDR. Our detection engineering is built from real
                exploit techniques, red team research, and live attack simulations &mdash; so we
                hunt for what attackers actually do, not what tools assume they&rsquo;ll do.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* The offensive edge — proprietary tooling */}
        <ScrollReveal direction="up" delay={100}>
          <div className="flex flex-col gap-6">
            <SectionHeading>The offensive edge - Proprietary red team tooling</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              {tools.map((tool) => (
                <div key={tool.title} className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <span aria-hidden className="mt-[3px] h-6 w-[3px] flex-shrink-0 bg-primary" />
                    <p className="headline-4 text-text-highlight">
                      {tool.title}
                      <br />
                      <span className="text-primary">{tool.subtitle}</span>
                    </p>
                  </div>
                  <p className="body-1 pl-[11px] text-text-highlight">{tool.description}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* How it's delivered — pipeline of 5 steps (pattern per DarkSight) */}
        <ScrollReveal direction="up" delay={150}>
          <div className="flex flex-col gap-4">
            <SectionHeading>How it&rsquo;s delivered</SectionHeading>
            <div className="flex flex-col lg:flex-row lg:items-center gap-0">
              {deliverySteps.map((step, i) => (
                <Fragment key={step}>
                  <div className="lg:flex-none">
                    <div className="flex h-full items-center justify-center rounded-[8px] bg-gradient-to-b from-[#2e353a] from-[14%] to-background p-4 min-h-[60px]">
                      <span className="body-1-heavy whitespace-nowrap text-center text-text-highlight">
                        {step}
                      </span>
                    </div>
                  </div>

                  {/* Connector spanning the gap between steps: an arrowhead
                      flush to the card the pulse leaves, a line out of it, and a
                      ring+dot sliding along that line into the next card. Five
                      steps leave the desktop gap at ~96px against the stacked
                      mobile one's 64px, so mobile is the same figure turned 90°
                      with the marker and the loop scaled to the shorter path. */}
                  {i < deliverySteps.length - 1 && (
                    <>
                      {/* Desktop — horizontal */}
                      <div
                        aria-hidden
                        className="relative z-10 hidden lg:flex lg:flex-1 items-center min-w-[36px]"
                      >
                        {/* Line runs behind, from the arrowhead into the dot centre so it reads as joined */}
                        <span className="absolute left-[6px] right-0 h-[2px] bg-primary" />
                        {/* Travelling pulse: a red dot slides along the line from the
                            arrowhead to the destination ring, then loops. */}
                        <span aria-hidden className="mdr-run-dot" />
                        {/* Arrowhead ▶ flush to the previous card */}
                        <span className="relative block h-0 w-0 border-y-[6px] border-l-[10px] border-y-transparent border-l-primary" />
                        {/* Static destination ring hidden — only the travelling ring shows. */}
                      </div>

                      {/* Mobile — vertical, mirroring the desktop order: line
                          behind, travelling pulse, then the ▼ head painted on
                          top at the start (flush to the card above). */}
                      <div aria-hidden className="lg:hidden relative z-10 h-16 w-full">
                        <span className="absolute top-[6px] bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-primary" />
                        <span aria-hidden className="mdr-run-dot-v" />
                        <span className="absolute top-0 left-1/2 -translate-x-1/2 block h-0 w-0 border-x-[6px] border-t-[10px] border-x-transparent border-t-primary" />
                      </div>
                    </>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Why enterprises switch */}
        <ScrollReveal direction="up" delay={200}>
          <div className="flex flex-col gap-4">
            <SectionHeading>Why enterprises switch to Offensive MDR</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              {benefits.map((benefit) => (
                <div key={benefit.label} className="flex items-start gap-4">
                  <span
                    aria-hidden
                    className="mt-[7px] h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                  />
                  <p className="text-text-highlight">
                    <span className="body-1-heavy">{benefit.label}</span>
                    <br />
                    <span className="body-1">{benefit.description}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* "How it's delivered" connectors — travelling red pulse arrowhead → ring */}
      <style jsx>{`
        /* Travelling marker is the ring+dot itself (matches the destination ⊙),
           so the whole circle slides along the line. */
        .mdr-run-dot {
          position: absolute;
          top: 50%;
          left: 12px;
          width: 19px;
          height: 19px;
          margin: -9.5px 0 0 -9.5px;
          border-radius: 9999px;
          border: 1.5px solid #ec1938;
          background: transparent;
          box-shadow: 0 0 8px 1px rgba(236, 25, 56, 0.5);
          animation: mdrRunDot 2s linear infinite;
        }
        .mdr-run-dot::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 11px;
          height: 11px;
          transform: translate(-50%, -50%);
          border-radius: 9999px;
          background: #ec1938;
        }
        @keyframes mdrRunDot {
          0% {
            left: 12px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 1;
          }
        }

        /* Vertical variant for the stacked mobile pipeline — the horizontal rule
           above with every axis swapped, keeping its 12px start offset. Two
           things are scaled for the phone: a 13px ring with a tighter halo (the
           19px ring and its 8px/1px glow read as a ~37px blob on a 64px
           connector), and a 1.25s loop, since 51.6px of travel at that duration
           lands on ~41px/s — the pace of the 83px desktop run over 2s. */
        .mdr-run-dot-v {
          position: absolute;
          left: 50%;
          top: 12px;
          width: 13px;
          height: 13px;
          margin: -6.5px 0 0 -6.5px;
          border-radius: 9999px;
          border: 1.5px solid #ec1938;
          background: transparent;
          box-shadow: 0 0 5px 0 rgba(236, 25, 56, 0.45);
          animation: mdrRunDotV 1.25s linear infinite;
        }
        .mdr-run-dot-v::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 7px;
          height: 7px;
          transform: translate(-50%, -50%);
          border-radius: 9999px;
          background: #ec1938;
        }
        @keyframes mdrRunDotV {
          0% {
            top: 12px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .mdr-run-dot,
          .mdr-run-dot-v {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};

export default OffensiveMDR;
